/* eslint-disable no-undef */


import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { Map, AdvancedMarker, useMap, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

import styles from './map.module.scss';

const MAP_LOCATION_DATA = process.env.REACT_APP_MAP_LOCATION_DATA;

export const ClusterContext = React.createContext({
  addMarker: (marker) => { }
});

const clusterRenderer = () => {
  return {
    render: ({ count, position }) => {
      const pinTag = document.createElement('div');
      pinTag.className = styles.marker;
      pinTag.innerHTML = `
        <div class="${styles.marker}">
          <div class="${styles.pin}">
            <img src='/images/marker.svg' alt='Map Marker'/>
          </div>
          <div class="${styles.tooltip}">
            <div class="${styles.name}">${count} Sites</div>
          </div>
        </div>
      `;

      pinTag.addEventListener('mouseover', () => {
        pinTag.classList.add(styles.active);
      })

      pinTag.addEventListener('mouseout', () => {
        pinTag.classList.remove(styles.active);
      })

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        // adjust zIndex to be above other markers
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        content: pinTag
      });

      return marker;
    }
  }
}

export const MapWidget = (props) => {
  const searchParams = new URLSearchParams(document.location.search)
  const zoom = searchParams.get('zoom');
  const location = searchParams.get('location');
  const streetView = searchParams.get('streetView');

  const [locations, setLocations] = useState([]);
  const [settings, setSettings] = useState({
    zoom: 17,
    centerPosition: null,
    streetView: true
  });


  const mapObject = useMap();
  const clusterRef = React.useRef();

  const fetchMapData = () => {
    fetch(MAP_LOCATION_DATA)
      .then(response => response.json())
      .then(response => {
        setLocations(response);
      })
  }

  const populateSettings = () => {
    var updated = {

    }

    if (zoom) {
      updated = {
        ...updated,
        zoom: parseInt(zoom)
      }
    }

    if (location) {
      var latlong = location.split(',');
      var latitude = parseFloat(latlong[0]);
      var longitude = parseFloat(latlong[1]);

      const parseLocation = new window.google.maps.LatLng(latitude, longitude);

      updated = {
        ...updated,
        centerPosition: parseLocation
      }
    }

    if (streetView) {
      updated = {
        ...updated,
        streetView: streetView === 'true'
      }
    }

    setSettings((state) => {
      return {
        ...state,
        ...updated
      }
    })
  }

  useEffect(() => {
    fetchMapData();
  }, []);

  useEffect(() => {
    if (props.loaded) {
      populateSettings();
    }
  }, [props.loaded]);

  useEffect(() => {
    if (mapObject) {
      clusterRef.current = new MarkerClusterer({
        map: mapObject,
        renderer: clusterRenderer()
      });
    }
  }, [mapObject]);

  useEffect(() => {
    if ((!mapObject || !mapObject.fitBounds) && locations.length == 0) {
      return;
    }

    if (!window.google.maps.LatLngBounds) {
      return;
    }

    if (settings.centerPosition) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    locations.forEach(location => {
      if (location.url) {
        bounds.extend({
          lat: location.latitude,
          lng: location.longitude,
        })
      }
    });

    if (mapObject && mapObject.fitBounds) {
      mapObject.fitBounds(bounds);
    }

  }, [window.google, mapObject, locations]);

  const handleMarkerAdded = (marker) => {
    clusterRef.current.addMarker(marker, false);
  }

  const handleZoomChanged = () => {
    if (mapObject && mapObject.getZoom) {
      const zoom = mapObject.getZoom();
      setSettings(settings => {
        return {
          ...settings,
          zoom: zoom
        }
      })
    }
  }

  const [clusterContext, setClusterContext] = useState({
    addMarker: handleMarkerAdded
  });

  return (
    <ClusterContext.Provider value={clusterContext}>
      <Map
        mapId={'3516be3ddf88ef44'}
        zoom={zoom ? settings.zoom : null}
        defaultZoom={settings.zoom}
        center={settings.centerPosition}
        gestureHandling={'greedy'}
        zoomControl={true}
        mapTypeControl={false}
        scaleControl={false}
        streetViewControl={settings.streetView}
        rotateControl={false}
        fullscreenControl={false}
        onZoomChanged={handleZoomChanged}
      >
        {locations.map(location => {
          return (
            <LocationMarker key={location.id} location={location} />
          );
        })}
      </Map>
    </ClusterContext.Provider>
  );
}

const LocationMarker = ({ location }) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [hover, setHover] = useState(false);
  const clusterContext = React.useContext(ClusterContext);

  const handleMouseOver = () => {
    setHover(true);
  }

  const handleMouseOut = () => {
    setHover(false);
  }

  const handleClick = () => {
    if (location.url) {
      window.open(location.url, "_blank");
    }
  }

  useEffect(() => {
    if (marker && marker.element) {
      clusterContext.addMarker(marker);
      marker.element.addEventListener("mouseover", handleMouseOver);
      marker.element.addEventListener("mouseout", handleMouseOut);
    }

    return () => {
      if (marker && marker.element) {
        marker.element.removeListener("mouseover", handleMouseOver);
        marker.element.removeListener("mouseout", handleMouseOut);
      }

    }
  }, [marker]);


  return (
    <AdvancedMarker
      key={location.id}
      zIndex={hover ? Number(google.maps.Marker.MAX_ZINDEX) : 1}
      ref={markerRef}
      position={{ lat: location.latitude, lng: location.longitude }}
      onClick={handleClick}
    >
      <div className={cx(styles.marker, {
        [styles.active]: hover
      })}>
        <div className={styles.pin}>
          <img src='/images/marker.svg' alt='Map Marker' />
        </div>
        <div className={styles.tooltip}>
          <div className={styles.name}>{location.short_name}</div>
        </div>
        {location.icon &&
          <div className={styles.icon}>
            <img alt="" src={`/icons/${location.id}.png`} />
          </div>
        }
      </div>
    </AdvancedMarker>
  );
}